import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Order.css";

type Trade = {
  tradeId: number;
  digitalCoinName: string;
  expectedProfit: number;
  tradeDuration: number;
  tradingAmount: number;
  tradingDateTimeUTC: string;
};

const Order = () => {
  const [activeTab, setActiveTab] = useState<"holding" | "history">("holding");
  const [historyList, setHistoryList] = useState<Trade[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("authToken");

  useEffect(() => {
    if (activeTab === "history" && userId) {
      setHistoryList([]); // Reset history list on tab switch
      setPage(1);
      fetchHistoryList(1);
    }
  }, [activeTab, userId]);

  const fetchHistoryList = async (pageNumber: number) => {
    if (!userId || !token) {
      setError("User is not authenticated. Please log in again.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://cryptoapi.dmmbitusa.com/api/Trade/GetAllTradesByUserId?userId=${userId}&page=${pageNumber}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data: Trade[] = await response.json();
      setHistoryList((prev) => [...prev, ...data]);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 20 && !loading) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [loading]);

  useEffect(() => {
    if (activeTab === "history" && page > 1) {
      fetchHistoryList(page);
    }
  }, [page, activeTab]);

  return (
    <div className="container">
      <h1 className="heading">Time Limit</h1>
      <div className="tabs">
        <button
          onClick={() => setActiveTab("holding")}
          className={`tab-button ${activeTab === "holding" ? "active" : ""}`}
        >
          Holding List
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`tab-button ${activeTab === "history" ? "active" : ""}`}
        >
          History List
        </button>
      </div>

      <div className="content">
        {activeTab === "holding" && (
          <div className="no-records">
            <p>No Records</p>
          </div>
        )}

        {activeTab === "history" && (
          <>
            {error && <p className="error">{error}</p>}

            {historyList.length === 0 && !loading ? (
              <div className="no-records">
                <img src="clipboard-icon.png" alt="No Records" />
                <p>No Records</p>
              </div>
            ) : (
              <div ref={scrollContainerRef} className="history-container" onScroll={handleScroll}>
                {historyList.map((trade) => (
                  <div key={trade.tradeId} className="trade-item">
                    <div className="trade-header">
                      <span className="coin-name">{trade.digitalCoinName}</span>
                      <span
                        className={`trade-status ${trade.expectedProfit >= 0 ? "check-up" : "check-down"}`}
                      >
                        {trade.expectedProfit >= 0 ? "✅ CheckUp" : "❌ CheckDown"}
                      </span>
                      <span
                        className={`profit ${trade.expectedProfit >= 0 ? "profit-up" : "profit-down"}`}
                      >
                        {trade.expectedProfit >= 0 ? `+${trade.expectedProfit}` : trade.expectedProfit} $
                      </span>
                    </div>
                    <div className="trade-details">
                      <p>
                        <strong>Deliver time:</strong> {trade.tradeDuration}s
                      </p>
                      <p>
                        <strong>Order amount:</strong> ${trade.tradingAmount.toFixed(2)}
                      </p>
                      <p>
                        <strong>Date:</strong> {new Date(trade.tradingDateTimeUTC).toLocaleString()}
                      </p>
                    </div>
                    <p className="order-status">Order Completed</p>
                  </div>
                ))}
                {loading && <p>Loading...</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Order;
